import type { BillingStatus, BlockedReason, UsageOverview } from '@/billing/api';
import { balanceCredits, planEndedAt, planState } from '@/billing/plan-math';
import { formatCopy } from '@/i18n/ns';
import { routeFor } from '@/workspace/navigation';
import { WorkspaceIcon } from '@/workspace/icons';
import { BalanceChart } from './balance-chart';
import { credits, planCredits, type Formatters } from './format';
import type { UsageCopy } from './copy';

const NEXT_MARKER_DAYS = 7;

export interface BalanceCardProps {
  overview: UsageOverview;
  status: BillingStatus | null;
  days: string[];
  spend: number[];
  now: Date;
  canChangePlan: boolean;
  native: boolean;
  f: Formatters;
  copy: UsageCopy;
  /** The paused text when the last payment failed (billing copy), if it did. */
  pastDue: string | null;
  onChangePlan: () => void;
}

function planFacts(props: BalanceCardProps) {
  const { overview, status } = props;
  const account = overview.account;
  const plans = [...(status?.plans ?? []), ...(status?.assignedPlan ? [status.assignedPlan] : [])];
  const current = planCredits(account.planId, plans);
  const next = account.nextPlanId ? planCredits(account.nextPlanId, plans) : null;
  return { account, current, next, renewsAt: account.periodEnd };
}

/** Why a plan that exists is not active: its last payment failed, or it ended. */
function inactivePart(props: BalanceCardProps, state: ReturnType<typeof planState>) {
  const { f, copy, overview, now } = props;
  if (state === 'payment_failed') return copy.planPaymentFailed;
  if (state !== 'ended') return null;
  const ended = planEndedAt(overview.account, now);
  return ended ? formatCopy(copy.planEndedOn, { date: f.date(ended) }) : copy.planEnded;
}

function PlanLine({ props }: { props: BalanceCardProps }) {
  const { f, copy } = props;
  const { account, current, next, renewsAt } = planFacts(props);
  const state = planState(account);
  const parts: string[] = [];
  const inactive = inactivePart(props, state);
  if (inactive) parts.push(inactive);
  if (account.planActive && current) {
    if (account.cancelAtPeriodEnd && renewsAt)
      parts.push(formatCopy(copy.planEnds, { date: f.date(renewsAt) }));
    else if (next && account.nextPlanAt)
      parts.push(
        formatCopy(copy.planSwitches, { credits: f.int(next), date: f.date(account.nextPlanAt) }),
      );
    else if (renewsAt)
      parts.push(formatCopy(copy.planRenews, { credits: f.int(current), date: f.date(renewsAt) }));
  }
  const discount = account.discount;
  if (discount)
    parts.push(
      discount.endsAt
        ? formatCopy(copy.discountUntil, {
            percent: f.percent(discount.percentOff),
            date: f.date(discount.endsAt),
          })
        : formatCopy(copy.discountOngoing, { percent: f.percent(discount.percentOff) }),
    );
  parts.push(copy.neverExpire);
  return (
    <p className="cu-plan-line">
      <strong>
        {state !== 'none' && current
          ? formatCopy(copy.planPerMonth, { credits: f.int(current) })
          : copy.noPlan}
      </strong>
      {parts.map((part) => (
        <span key={part} className="cu-plan-part">
          <span className="cu-sep" />
          <span>{part}</span>
        </span>
      ))}
    </p>
  );
}

function noticeText(props: BalanceCardProps, reason: BlockedReason | null) {
  const { status, f, copy } = props;
  const { account, current, next, renewsAt } = planFacts(props);
  const owner = status?.canManage === true;
  if (reason === 'plan_required' && props.pastDue) return props.pastDue;
  if (reason === 'plan_required') return owner ? copy.noticePlanOwner : copy.noticePlanMember;
  if (reason === 'credits_exhausted') {
    if (!account.planActive || !renewsAt || account.cancelAtPeriodEnd)
      return copy.noticeOutNoRenewal;
    return formatCopy(owner ? copy.noticeOutOwner : copy.noticeOutMember, {
      date: f.date(renewsAt),
      time: f.time(renewsAt),
      credits: f.int(next ?? current ?? 0),
    });
  }
  const limit = status?.memberLimit;
  if (reason === 'member_limit_reached' && limit)
    return formatCopy(copy.noticeLimit, {
      limit: f.int(credits(limit.limitMicros)),
      date: renewsAt ? f.date(renewsAt) : '',
    });
  if (limit && limit.usedMicros >= limit.limitMicros * 0.8)
    return formatCopy(copy.noticeLimitSoon, {
      used: f.int(credits(limit.usedMicros)),
      limit: f.int(credits(limit.limitMicros)),
    });
  return null;
}

/** The next renewal marker, when it falls within a week of the range's end. */
function nextGrant(props: BalanceCardProps) {
  const { overview, now } = props;
  const { account, current, next, renewsAt } = planFacts(props);
  if (!account.planActive || account.cancelAtPeriodEnd || !renewsAt || !current) return null;
  const ahead = new Date(renewsAt).getTime() - now.getTime();
  if (
    ahead <= 0 ||
    ahead > NEXT_MARKER_DAYS * 86_400_000 ||
    overview.to !== props.days[props.days.length - 1]
  )
    return null;
  return { at: renewsAt, credits: next ?? current, newPlan: Boolean(next) };
}

export function BalanceCard(props: BalanceCardProps) {
  const { overview, status, canChangePlan, native, f, copy, onChangePlan } = props;
  const reason = status?.paymentsEnabled ? (status.blockedReason ?? null) : null;
  const notice = noticeText(props, reason);
  const balance = balanceCredits(overview.account.creditBalanceMicros);
  const fixPayment =
    Boolean(props.pastDue) && reason === 'plan_required' && status?.canManage === true && !native;
  const cta =
    reason === 'credits_exhausted'
      ? copy.upgradeNow
      : overview.account.planActive
        ? copy.changePlan
        : copy.choosePlan;
  return (
    <section className="cu-card" aria-label={copy.balanceTitle}>
      <div className="cu-balance-top">
        <div>
          <h2 className="cu-card-title">
            {copy.balanceTitle}
            {reason && <span className="cu-paused-chip">{copy.paused}</span>}
          </h2>
          <div className="cu-hero">
            <span className="cu-hero-num">{f.int(balance)}</span>
            <span className="cu-hero-unit">{copy.creditsUnit}</span>
          </div>
          <PlanLine props={props} />
        </div>
        {canChangePlan && !native && (
          <button type="button" className="cu-btn is-primary is-lg" onClick={onChangePlan}>
            {cta}
          </button>
        )}
        {canChangePlan && native && <p className="cu-native-note">{copy.manageOnWeb}</p>}
      </div>
      {notice && (
        <div className={`cu-notice${reason ? '' : ' is-info'}`} role="status">
          <WorkspaceIcon name="alert" size={16} />
          <p>{notice}</p>
          {fixPayment && (
            <a className="cu-btn cu-notice-action" href={routeFor('billing')}>
              {copy.openBilling}
            </a>
          )}
        </div>
      )}
      {props.days.length > 0 && overview.scope === 'company' && (
        <BalanceChart {...props} next={nextGrant(props)} />
      )}
    </section>
  );
}
